import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FileCheck, 
  Shield, 
  Clock,
  Check,
  ArrowRight,
  ArrowLeft,
  Users,
  Building2,
  Truck,
  Lock,
  Zap,
  Brain,
  Database,
  QrCode,
  Bell,
  Share2,
  Search,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Fingerprint,
  Car,
  FileText,
  Bot,
  Blocks,
  Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackPageView, trackFormSubmit } from '../hooks/useAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Driver credential types
const driverCredentialTypes = [
  {
    icon: Fingerprint,
    title: 'Background Checks',
    description: 'Comprehensive criminal background checks, sex offender registry, and federal watchlist screening.',
    provider: 'Powered by Checkr',
    color: 'neon-purple'
  },
  {
    icon: Car,
    title: 'DMV Records',
    description: 'Driving history, license verification, violations, and MVR reports.',
    provider: 'State DMV Integration',
    color: 'neon-cyan'
  },
  {
    icon: FileCheck,
    title: 'Drug Screening',
    description: '5-panel, 10-panel, and DOT drug screens with rapid results and chain of custody.',
    provider: 'Powered by Occuscreen',
    color: 'neon-green'
  },
  {
    icon: FileText,
    title: 'Document Verification',
    description: 'License, insurance, certifications - AI-powered verification and expiration tracking.',
    provider: 'MedexOVA AI',
    color: 'yellow'
  }
];

// Provider/Company credential types (for broker verification)
const providerCredentialTypes = [
  {
    icon: Building2,
    title: 'Business Verification',
    description: 'State business license, EIN verification, years in operation, and ownership validation.',
    required: true
  },
  {
    icon: Truck,
    title: 'DOT & Authority',
    description: 'USDOT number, MC authority, operating status, and safety ratings verification.',
    required: true
  },
  {
    icon: Shield,
    title: 'Insurance Certificates',
    description: 'Auto liability, general liability, workers comp - with real-time COI tracking.',
    required: true
  },
  {
    icon: FileCheck,
    title: 'NPI & Medicaid',
    description: 'National Provider Identifier, Medicaid provider enrollment, and billing credentials.',
    required: true
  },
  {
    icon: Users,
    title: 'Fleet Compliance',
    description: 'Vehicle registrations, inspections, ADA compliance, and fleet size verification.',
    required: true
  },
  {
    icon: Lock,
    title: 'HIPAA Compliance',
    description: 'BAA agreements, privacy training records, and security attestations.',
    required: false
  }
];

// Key benefits
const keyBenefits = [
  {
    icon: Zap,
    title: 'Instant Verification',
    stat: '< 24hrs',
    description: 'Most credentials verified within hours, not weeks. Background checks in 1-3 days.'
  },
  {
    icon: Share2,
    title: 'Portable Credentials',
    stat: '1-Click',
    description: 'Drivers share verified credentials with any employer. No re-running checks.'
  },
  {
    icon: Lock,
    title: 'Blockchain Secured',
    stat: '100%',
    description: 'Immutable records. Tamper-proof verification. Complete audit trail.'
  },
  {
    icon: Bell,
    title: 'Auto Renewals',
    stat: '30 Days',
    description: 'AI alerts before credentials expire. Never miss a renewal deadline.'
  }
];

// Pricing tiers
const pricingTiers = [
  {
    name: 'Driver',
    price: '$4.99',
    period: '/month',
    description: 'For individual drivers',
    features: [
      'Digital credential wallet',
      'Secure document storage',
      'Share credentials instantly',
      'Expiration reminders',
      'QR code verification'
    ],
    cta: 'Get Early Access',
    popular: false
  },
  {
    name: 'Provider',
    price: '$49',
    period: '/month',
    description: 'Up to 25 drivers',
    features: [
      'Everything in Driver',
      'Fleet compliance dashboard',
      'Bulk driver onboarding',
      'Background check ordering',
      'Drug screen scheduling',
      'DMV record pulls',
      'Audit-ready reports'
    ],
    cta: 'Get Early Access',
    popular: true,
    savings: 'Save $75+ per driver vs traditional'
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: '25+ drivers',
    features: [
      'Everything in Provider',
      'Unlimited drivers',
      'API access',
      'Broker integrations',
      'Dedicated support',
      'Custom compliance rules',
      'White-label options'
    ],
    cta: 'Contact Sales',
    popular: false
  }
];

// Services pricing (a la carte)
const servicePricing = [
  { service: 'Background Check (Standard)', price: '$29.99', turnaround: '1-3 days' },
  { service: 'Background Check (Enhanced)', price: '$49.99', turnaround: '2-5 days' },
  { service: 'DMV/MVR Report', price: '$12.99', turnaround: '< 24 hours' },
  { service: '5-Panel Drug Screen', price: '$39.99', turnaround: '1-2 days' },
  { service: '10-Panel Drug Screen', price: '$54.99', turnaround: '1-2 days' },
  { service: 'DOT Drug Screen', price: '$64.99', turnaround: '1-2 days' }
];

export const CredentialingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    account_type: '',
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    driver_count: '',
    current_process: '',
    interested_services: [],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    trackPageView('/credentialing', 'credentialing');
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        interested_services: checked 
          ? [...prev.interested_services, value]
          : prev.interested_services.filter(s => s !== value)
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.account_type || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/products/signup`, {
        ...formData,
        product: 'credentialing',
        tags: ['credentialing', formData.account_type, `drivers_${formData.driver_count || 'unknown'}`],
        source: 'credentialing_landing_page'
      });
      
      trackFormSubmit('credentialing_signup', 'credentialing', { 
        account_type: formData.account_type,
        driver_count: formData.driver_count 
      });
      
      setIsSubmitted(true);
      toast.success('You\'re on the list! We\'ll notify you when we launch.');
    } catch (error) {
      console.error('Signup error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-medex-bg flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 rounded-full bg-neon-green/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-neon-green" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-white mb-4">
            Welcome to the Future of Credentialing!
          </h2>
          <p className="text-slate-400 mb-6">
            You're on the early access list for Medex Credentialing. We'll notify you 
            as soon as the platform launches with exclusive early adopter benefits.
          </p>
          <button onClick={() => navigate('/')} className="btn-secondary">
            Return to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-medex-bg">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-neon-green/10 via-transparent to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-neon-green/10 border border-neon-green/30 mb-6">
                <FileCheck className="w-4 h-4 text-neon-green" />
                <span className="text-sm text-neon-green font-medium">Centralized Credentialing</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
                One Credential. <span className="text-neon-green">Everywhere.</span>
              </h1>
              
              <p className="text-xl text-slate-400 mb-8">
                The NEMT industry's first blockchain-secured credential repository. 
                Drivers get verified once, share credentials anywhere. Providers onboard 
                drivers in hours, not weeks.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <a href="#signup" className="btn-primary inline-flex items-center space-x-2">
                  <span>Get Early Access</span>
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a href="#how-it-works" className="btn-secondary">
                  How It Works
                </a>
                <Link to="/portal/login" className="btn-secondary inline-flex items-center space-x-2 border-neon-green/50 text-neon-green hover:bg-neon-green/10">
                  <Users className="w-4 h-4" />
                  <span>Access Portal</span>
                </Link>
              </div>

              {/* Tech badges */}
              <div className="flex flex-wrap gap-3">
                <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                  <Blocks className="w-4 h-4 text-neon-purple" />
                  <span className="text-xs text-slate-300">Blockchain Verified</span>
                </div>
                <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                  <Brain className="w-4 h-4 text-neon-cyan" />
                  <span className="text-xs text-slate-300">AI-Powered</span>
                </div>
                <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                  <Bot className="w-4 h-4 text-neon-green" />
                  <span className="text-xs text-slate-300">24/7 AI Assistant</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              {/* Visual representation */}
              <div className="glass-card p-8 border-neon-green/30">
                <div className="flex items-center justify-center mb-6">
                  <div className="relative">
                    <div className="w-32 h-32 rounded-full bg-neon-green/20 flex items-center justify-center">
                      <QrCode className="w-16 h-16 text-neon-green" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-neon-cyan flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-black" />
                    </div>
                  </div>
                </div>
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold text-white mb-2">Driver Credential Passport</h3>
                  <p className="text-sm text-slate-400">One scan. Instant verification.</p>
                </div>
                <div className="space-y-3">
                  {['Background Check', 'Drug Screen', 'DMV Record', 'Insurance'].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <span className="text-slate-300">{item}</span>
                      <div className="flex items-center space-x-2">
                        <CheckCircle2 className="w-4 h-4 text-neon-green" />
                        <span className="text-xs text-neon-green">Verified</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 bg-medex-surface/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Credentialing is <span className="text-red-400">Broken</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Every time a driver changes companies or a provider onboards new drivers, 
              the same checks are run again. Time wasted. Money wasted.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { problem: '2-4 Weeks', desc: 'Average time to credential a new driver' },
              { problem: '$200+', desc: 'Cost per driver for background checks' },
              { problem: '3-5x', desc: 'Same checks run when drivers switch jobs' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6 border-red-500/20 text-center"
              >
                <div className="text-4xl font-heading font-bold text-red-400 mb-2">{item.problem}</div>
                <p className="text-slate-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              How <span className="text-neon-green">Medex Credentialing</span> Works
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              A centralized, blockchain-secured repository that follows drivers throughout their career.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                step: '01',
                title: 'Driver Gets Verified',
                desc: 'Complete background check, drug screen, and DMV pull once.',
                icon: Fingerprint
              },
              {
                step: '02',
                title: 'Credentials Stored',
                desc: 'Blockchain-secured storage. Tamper-proof. Always accessible.',
                icon: Database
              },
              {
                step: '03',
                title: 'Share Anywhere',
                desc: 'One-click sharing with any employer or broker via QR code.',
                icon: Share2
              },
              {
                step: '04',
                title: 'Auto Renewals',
                desc: 'AI monitors expirations and schedules renewals automatically.',
                icon: Bell
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="glass-card p-6 h-full">
                  <div className="text-5xl font-heading font-bold text-neon-green/20 mb-4">{item.step}</div>
                  <div className="w-12 h-12 rounded-xl bg-neon-green/10 flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-neon-green" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-6 h-6 text-neon-green/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Driver Credentials Services */}
      <section className="py-16 bg-medex-surface/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Driver <span className="text-neon-cyan">Credentials</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Comprehensive driver screening powered by industry-leading providers.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {driverCredentialTypes.map((cred, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`glass-card p-6 border-${cred.color}/30 hover:border-${cred.color}/50 transition-colors`}
              >
                <div className={`w-12 h-12 rounded-xl bg-${cred.color}/10 flex items-center justify-center mb-4`}>
                  <cred.icon className={`w-6 h-6 text-${cred.color}`} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{cred.title}</h3>
                <p className="text-sm text-slate-400 mb-4">{cred.description}</p>
                <div className="text-xs text-slate-500 flex items-center">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {cred.provider}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Provider/Company Credentials */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Provider <span className="text-neon-purple">Credentials</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Transportation company verification for brokers, stakeholders, and healthcare partners.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {providerCredentialTypes.map((cred, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`glass-card p-6 ${cred.required ? 'border-neon-purple/30' : 'border-white/10'} hover:border-neon-purple/50 transition-colors relative`}
              >
                {cred.required && (
                  <div className="absolute top-4 right-4">
                    <span className="text-xs bg-neon-purple/20 text-neon-purple px-2 py-0.5 rounded-full">REQUIRED</span>
                  </div>
                )}
                <div className="w-12 h-12 rounded-xl bg-neon-purple/10 flex items-center justify-center mb-4">
                  <cred.icon className="w-6 h-6 text-neon-purple" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{cred.title}</h3>
                <p className="text-sm text-slate-400">{cred.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Broker Benefits Callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 glass-card p-8 border-neon-cyan/30 bg-neon-cyan/5"
          >
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 rounded-2xl bg-neon-cyan/20 flex items-center justify-center flex-shrink-0">
                  <Search className="w-7 h-7 text-neon-cyan" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">For Brokers & Stakeholders</h3>
                  <p className="text-slate-400 max-w-xl">
                    Instantly verify provider compliance before contracting. Access real-time credential 
                    status, insurance certificates, and fleet compliance reports through our broker portal. 
                    Reduce onboarding time from weeks to hours.
                  </p>
                </div>
              </div>
              <a
                href="#signup"
                className="btn-primary whitespace-nowrap inline-flex items-center space-x-2"
              >
                <span>Request Broker Access</span>
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-neon-green/10 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-6 h-6 text-neon-green" />
                </div>
                <div className="text-3xl font-heading font-bold text-neon-green mb-2">{benefit.stat}</div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-slate-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-medex-surface/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Simple, <span className="text-neon-green">Affordable</span> Pricing
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              A fraction of traditional credentialing costs. Built for volume.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`glass-card p-8 relative ${tier.popular ? 'border-neon-green/50 scale-105' : ''}`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-neon-green text-black text-sm font-bold rounded-full">
                    MOST POPULAR
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold text-white mb-2">{tier.name}</h3>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-heading font-bold text-white">{tier.price}</span>
                    <span className="text-slate-400 ml-1">{tier.period}</span>
                  </div>
                  <p className="text-sm text-slate-400 mt-2">{tier.description}</p>
                  {tier.savings && (
                    <p className="text-xs text-neon-green mt-2">{tier.savings}</p>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start space-x-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-neon-green flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <a 
                  href="#signup" 
                  className={`w-full inline-flex items-center justify-center ${tier.popular ? 'btn-primary' : 'btn-secondary'}`}
                >
                  {tier.cta}
                </a>
              </motion.div>
            ))}
          </div>

          {/* Service Pricing Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 max-w-3xl mx-auto"
          >
            <h3 className="text-xl font-semibold text-white text-center mb-6">Individual Service Pricing</h3>
            <div className="glass-card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-white/5">
                    <th className="text-left p-4 text-sm font-medium text-slate-300">Service</th>
                    <th className="text-center p-4 text-sm font-medium text-slate-300">Price</th>
                    <th className="text-center p-4 text-sm font-medium text-slate-300">Turnaround</th>
                  </tr>
                </thead>
                <tbody>
                  {servicePricing.map((item, index) => (
                    <tr key={index} className="border-t border-white/5">
                      <td className="p-4 text-sm text-white">{item.service}</td>
                      <td className="p-4 text-sm text-neon-green text-center font-semibold">{item.price}</td>
                      <td className="p-4 text-sm text-slate-400 text-center">{item.turnaround}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Signup Form */}
      <section id="signup" className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Get <span className="text-neon-green">Early Access</span>
            </h2>
            <p className="text-lg text-slate-400">
              Be first to access the future of NEMT credentialing.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="glass-card p-8"
          >
            {/* Account Type Selection */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-slate-300 mb-4">
                I am a... <span className="text-red-400">*</span>
              </label>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: 'driver', label: 'Driver', icon: Users },
                  { value: 'provider', label: 'Provider', icon: Building2 },
                  { value: 'broker', label: 'Broker', icon: Search }
                ].map((type) => (
                  <label
                    key={type.value}
                    className={`flex flex-col items-center p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.account_type === type.value
                        ? 'border-neon-green bg-neon-green/10'
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                    }`}
                  >
                    <input
                      type="radio"
                      name="account_type"
                      value={type.value}
                      checked={formData.account_type === type.value}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <type.icon className={`w-6 h-6 mb-2 ${formData.account_type === type.value ? 'text-neon-green' : 'text-slate-400'}`} />
                    <span className={formData.account_type === type.value ? 'text-white' : 'text-slate-400'}>{type.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {formData.account_type === 'driver' ? 'Full Name' : 'Company Name'} <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                  placeholder={formData.account_type === 'driver' ? 'John Smith' : 'Company Name'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Contact Name
                </label>
                <input
                  type="text"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  className="w-full input-dark"
                  placeholder="Contact person"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full input-dark"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              {formData.account_type !== 'driver' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Number of Drivers
                  </label>
                  <select
                    name="driver_count"
                    value={formData.driver_count}
                    onChange={handleChange}
                    className="w-full input-dark"
                  >
                    <option value="">Select range</option>
                    <option value="1-10">1-10 drivers</option>
                    <option value="11-25">11-25 drivers</option>
                    <option value="26-50">26-50 drivers</option>
                    <option value="51-100">51-100 drivers</option>
                    <option value="100+">100+ drivers</option>
                  </select>
                </div>
              )}
            </div>

            {/* Services Interest */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-4">
                Interested Services
              </label>
              <div className="grid grid-cols-2 gap-3">
                {['Background Checks', 'Drug Screening', 'DMV Records', 'Document Storage'].map((service) => (
                  <label key={service} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="interested_services"
                      value={service}
                      checked={formData.interested_services.includes(service)}
                      onChange={handleChange}
                      className="rounded border-white/20 bg-white/5 text-neon-green focus:ring-neon-green"
                    />
                    <span className="text-sm text-slate-300">{service}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* SMS Opt-In Section */}
            <div className="mb-6 p-4 rounded-xl bg-white/5 border border-neon-cyan/20">
              <label className="block text-sm font-semibold text-white mb-3">
                Sign up for SMS:
              </label>
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="sms_opt_in"
                  name="sms_opt_in"
                  checked={formData.sms_opt_in || false}
                  onChange={handleChange}
                  className="mt-1 w-5 h-5 rounded border-white/20 bg-white/5 text-neon-cyan focus:ring-neon-cyan/50 cursor-pointer"
                />
                <label htmlFor="sms_opt_in" className="text-sm text-slate-300 cursor-pointer leading-relaxed">
                  <span className="font-medium text-white">I agree to receive automated SMS text messages from MedexOVA</span> at the phone number provided above regarding credential updates, compliance alerts, and important notifications.
                  <span className="block mt-2 text-xs text-slate-400">
                    Message frequency may vary. Standard message and data rates may apply. Reply <span className="font-semibold text-neon-cyan">STOP</span> to opt out. Reply <span className="font-semibold text-neon-cyan">HELP</span> for assistance. Your mobile information will not be sold or shared with third parties for promotional or marketing purposes.
                  </span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <span>Get Early Access</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            <p className="text-xs text-slate-500 text-center mt-4">
              Early adopters receive exclusive pricing and priority access.
            </p>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default CredentialingPage;
